<script setup lang="ts">
  import { onMounted, ref } from 'vue';
  import { DatePicker, IconImage, SearchCombobox, SelectInput, SimpleLoading } from '@/shared/ui';
  import { formatDateToPtBR, toNumberOrUndefined } from '@/shared/lib';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import type { useRoleChangeForm } from '../../model/useRoleChangeForm';
  import OnboardingMinistranteRow from '../onboarding/OnboardingMinistranteRow.vue';
  import CreateTrainingDocumentsTable from '../CreateTrainingDocumentsTable.vue';

  const props = defineProps<{
    form: ReturnType<typeof useRoleChangeForm>;
  }>();

  const scheduledDatePickerOpen = ref(false);
  const ministranteDatePickers = ref<Record<number, boolean>>({});

  function getMinistranteDatePickerOpen(rowId: number): boolean {
    return ministranteDatePickers.value[rowId] ?? false;
  }

  function setMinistranteDatePickerOpen(rowId: number, open: boolean): void {
    ministranteDatePickers.value[rowId] = open;
  }

  onMounted(async () => {
    await props.form.load();
  });
</script>

<template>
  <div
    v-if="form.loading.value"
    data-testid="role-change-form-loading"
    class="flex w-full justify-center py-12"
  >
    <SimpleLoading />
  </div>

  <template v-else>
    <div
      data-testid="role-change-form"
      class="flex flex-col gap-1.5 border-b border-gray-02 px-6 py-8"
    >
      <span
        id="role-change-title-label"
        class="text-sm font-semibold text-gray-07"
        >Título do Treinamento:</span
      >
      <input
        :value="form.title.value"
        type="text"
        data-testid="role-change-title-input"
        aria-labelledby="role-change-title-label"
        placeholder="Escreva um título para identificar o treinamento"
        class="w-full rounded-lg border border-gray-02 px-4 py-3 text-sm text-gray-07 shadow-xs placeholder:text-gray-03 focus:border-gray-07 focus:outline-none"
        @input="form.title.value = ($event.target as HTMLInputElement).value"
      />
    </div>

    <div class="flex flex-col gap-4 px-6 py-8">
      <span class="text-sm font-semibold text-gray-07">Programação do Treinamento</span>
      <div class="grid grid-cols-[304px_348px_348px] gap-4">
        <div class="flex flex-col gap-1.5">
          <span
            id="role-change-scheduled-by-member-label"
            class="text-sm font-semibold text-gray-07"
            >Responsável pela Programação:</span
          >
          <SelectInput
            :model-value="form.scheduledByMemberId.value ?? ''"
            :options="form.memberOptions.value"
            label="Selecione um colaborador"
            aria-labelledby="role-change-scheduled-by-member-label"
            @update:model-value="form.setScheduledByMemberId(toNumberOrUndefined($event) ?? null)"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            id="role-change-scheduled-by-position-label"
            class="text-sm font-semibold text-gray-07"
            >Cargo:</span
          >
          <SelectInput
            :model-value="form.scheduledByPositionId.value ?? ''"
            :options="form.scheduledByPositions.value"
            label="Selecione o cargo relacionado ao treinamento"
            aria-labelledby="role-change-scheduled-by-position-label"
            @update:model-value="form.scheduledByPositionId.value = toNumberOrUndefined($event) ?? null"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            id="role-change-scheduled-date-label"
            class="text-sm font-semibold text-gray-07"
            >Data da programação:</span
          >
          <button
            type="button"
            data-testid="role-change-scheduled-date-trigger"
            aria-labelledby="role-change-scheduled-date-label"
            aria-haspopup="dialog"
            :aria-expanded="scheduledDatePickerOpen"
            class="flex w-full items-center gap-2 rounded-lg border border-[var(--color-border-color)] bg-white-01 px-4 py-2.5 text-sm whitespace-nowrap focus:outline-none"
            @click="scheduledDatePickerOpen = true"
          >
            <IconImage
              :src="CalendarIcon"
              alt="Calendário"
              class="h-5 w-5 shrink-0"
            />
            <span :class="form.scheduledDate.value ? 'text-gray-07' : 'text-gray-03'">
              {{ form.scheduledDate.value ? formatDateToPtBR(form.scheduledDate.value) : 'Selecione uma data' }}
            </span>
          </button>
          <DatePicker
            :selected-date="form.scheduledDate.value"
            :is-open="scheduledDatePickerOpen"
            @update:selected-date="form.scheduledDate.value = $event"
            @update:is-open="scheduledDatePickerOpen = $event"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-4 border-b border-gray-02 px-6 pb-8">
      <div class="grid grid-cols-[304px_348px_348px] gap-4">
        <div class="flex flex-col gap-1.5">
          <span
            id="role-change-trainee-member-label"
            class="text-sm font-semibold text-gray-07"
            >Colaborador a ser treinado:</span
          >
          <SelectInput
            :model-value="form.traineeMemberId.value ?? ''"
            :options="form.memberOptions.value"
            label="Selecione um colaborador"
            aria-labelledby="role-change-trainee-member-label"
            @update:model-value="form.setTraineeMemberId(toNumberOrUndefined($event) ?? null)"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            id="role-change-trainee-position-label"
            class="text-sm font-semibold text-gray-07"
            >Cargo:</span
          >
          <SelectInput
            :model-value="form.traineePositionId.value ?? ''"
            :options="form.traineePositions.value"
            label="Selecione o cargo relacionado ao treinamento"
            aria-labelledby="role-change-trainee-position-label"
            @update:model-value="form.traineePositionId.value = toNumberOrUndefined($event) ?? null"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            id="role-change-matrix-revision-label"
            class="text-sm font-semibold text-gray-07"
            >Matriz de treinamento:</span
          >
          <SearchCombobox
            :model-value="form.trainingMatrixRevisionId.value ? String(form.trainingMatrixRevisionId.value) : ''"
            :options="form.revisionComboboxOptions.value"
            placeholder="Selecione uma revisão"
            trigger-icon="arrow-down"
            aria-labelledby="role-change-matrix-revision-label"
            class="h-11 rounded-lg border-[var(--color-border-color)] bg-white-01 px-4 py-2.5 shadow-none"
            @update:model-value="form.trainingMatrixRevisionId.value = toNumberOrUndefined($event) ?? null"
            @search="form.setRevisionSearchTerm($event)"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8">
      <span class="text-sm font-semibold text-gray-07">Documentos</span>
      <CreateTrainingDocumentsTable
        selectable
        :documents="form.documents.documents.value"
        :loading="form.documents.loading.value"
        :is-selected="form.documents.isSelected"
        :all-selected="form.documents.allSelected.value"
        :partially-selected="form.documents.partiallySelected.value"
        :selected-count="form.documents.selectedCount.value"
        @view="form.documents.viewDocumentRevision"
        @toggle="form.documents.toggle"
        @toggle-all="form.documents.toggleAll"
      />
    </div>

    <div
      v-for="(row, index) in form.ministrantes.rows.value"
      :key="row.id"
      class="border-b border-gray-02 px-6 py-8"
    >
      <OnboardingMinistranteRow
        :row="row"
        :members="form.members.value"
        :member-options="form.memberOptions.value"
        :is-first="index === 0"
        :is-add-disabled="!form.ministrantes.canAddMore.value"
        :date-picker-open="getMinistranteDatePickerOpen(row.id)"
        @update:responsible-member-id="form.ministrantes.setResponsibleMemberId(index, $event)"
        @update:responsible-user-position-id="form.ministrantes.setResponsibleUserPositionId(index, $event)"
        @update:training-date="form.ministrantes.setTrainingDate(index, $event)"
        @update:date-picker-open="setMinistranteDatePickerOpen(row.id, $event)"
        @add="form.ministrantes.addRow()"
        @remove="form.ministrantes.removeRow(index)"
      />
    </div>

    <div class="flex flex-col gap-1.5 px-6 py-8">
      <span class="text-sm font-semibold text-gray-07">Observações:</span>
      <textarea
        :value="form.notes.value"
        rows="4"
        data-testid="role-change-notes-input"
        placeholder="Descreva qualquer observação sobre o treinamento"
        class="w-full resize-none rounded-lg border border-gray-02 p-4 text-sm text-gray-07 shadow-xs placeholder:text-gray-03 focus:border-gray-07 focus:outline-none"
        @input="form.notes.value = ($event.target as HTMLTextAreaElement).value"
      />
    </div>
  </template>
</template>
